import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { RealtimeEventTypes } from '@interfaces/realtime/realtime-envelope.interface';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { AuthenticationService } from '@services/http/authentication.service';
import { RealtimeService } from '@services/realtime/realtime.service';
import { selectUserId } from '@state/auth/auth.selectors';
import { AppState } from '@state/index';
import { debounceTime, first } from 'rxjs';

/**
 * Permissões que refletem na hora (PBI 3499). Quando alguém muda as permissões, os grupos ou o escopo de empresas de um
 * usuário, o backend publica `user.permissions-changed` no tópico dele; este serviço troca o token e recarrega as
 * permissões da aba aberta, sem novo login. No F5 (app aberto com sessão guardada) só recarrega as permissões, para
 * cobrir a aba que estava fechada ou sem conexão na hora da mudança: trocar o token a cada F5 gravaria um Login na
 * auditoria e o "último login" toda vez. Perfil e empresa trocados com a aba fechada chegam na renovação de sempre
 * (perto de vencer). Falha não derruba a sessão: o token antigo segue valendo.
 */
@Injectable({ providedIn: 'root' })
export class SessionPermissionsSyncService {
  private readonly auth = inject(AuthenticationService);
  private readonly realtime = inject(RealtimeService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Uma renovação por vez (evento repetido ou F5 junto com evento). */
  private syncing: Promise<void> | null = null;

  /** Liga o F5 e o tempo real. Chamado uma vez pelo `AppComponent`. */
  start(): void {
    if (!this.isBrowser) {
      return;
    }

    // F5: a sessão já veio do navegador (estado reidratado antes de o app subir). Login novo não passa por aqui,
    // porque o login já busca token e permissões.
    this.store
      .select(selectUserId)
      .pipe(first())
      .subscribe(userId => {
        if (userId) {
          void this.auth.reloadPermissions().catch(() => undefined);
        }
      });

    this.realtime
      .on(RealtimeEventTypes.UserPermissionsChanged)
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.sync(true));
  }

  /**
   * Troca o token e recarrega as permissões (aviso de tempo real).
   * @param notify Mostra o aviso ao usuário (mudança feita por outra pessoa enquanto a tela estava aberta).
   */
  private sync(notify: boolean): Promise<void> {
    if (this.syncing) {
      return this.syncing;
    }
    this.syncing = this.auth
      .refreshSessionAndPermissions()
      .then(renewed => {
        if (renewed && notify) {
          this.toast.showToastInfo(
            'Suas permissões foram atualizadas',
            'A tela já mostra o que você pode fazer agora, sem precisar sair e entrar de novo.'
          );
        }
      })
      .catch(() => undefined)
      .finally(() => {
        this.syncing = null;
      });
    return this.syncing;
  }
}
